/**
 * CaptureThumbs (#39) — the ONE photo-thumbnail surface, shared by the Daily
 * Logs list (DailyLog) and the re-opened save screen (canon `saveFrom`) so the
 * two never drift.
 *
 * Renders a capture's `primary_photo` blobs as real image thumbnails, read from
 * the durable IndexedDB blob store (`getBlobsForCapture`) — the media IS
 * retrievable to the PWA locally; the log/review surfaces simply used to render
 * a count-based grey PLACEHOLDER box instead of the actual photo. Object URLs
 * are built on mount and revoked (guarded) on unmount. Renders nothing when
 * there are no local photo blobs (jsdom without createObjectURL, or a
 * delivered-then-purged / seeded-remote entry) — never a broken image; the
 * caller's "N photos" count still stands as the fallback signal. A server-GET
 * fetch for purged-but-delivered media rides the gated read transport (#12).
 */
import { useEffect, useState } from "preact/hooks";
import { getBlobsForCapture } from "../outbox/db";

/** Object URLs for a capture's primary_photo blobs (durable IDB store). Built on
 * mount, revoked (guarded) on unmount. `[]` in jsdom/no-createObjectURL and for
 * a capture with no local photo blobs. */
export function useCaptureThumbnails(capture_id: string): string[] {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const canUrl = typeof URL !== "undefined" && typeof URL.createObjectURL === "function";
    if (!canUrl) return;
    let made: string[] = [];
    let cancelled = false;
    void getBlobsForCapture(capture_id).then((parts) => {
      if (cancelled) return;
      made = parts.filter((p) => p.role === "primary_photo").map((p) => URL.createObjectURL(p.blob));
      setUrls(made);
    });
    return () => {
      cancelled = true;
      // Guard revokeObjectURL too — it may be absent at unmount (jsdom, or a
      // stub torn down); an unconditional call would throw during cleanup.
      if (typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
        for (const u of made) URL.revokeObjectURL(u);
      }
    };
    // capture_id identifies the entry; rebuild thumbs once per entry.
  }, [capture_id]);
  return urls;
}

export interface CaptureThumbsProps {
  capture_id: string;
  /** Thumbnail edge length in px. */
  size: number;
  /** Testid for the strip container. */
  stripTestid: string;
  /** Testid prefix for each thumbnail (`${itemTestid}-${i}`). */
  itemTestid: string;
}

export function CaptureThumbs({ capture_id, size, stripTestid, itemTestid }: CaptureThumbsProps) {
  const urls = useCaptureThumbnails(capture_id);
  if (urls.length === 0) return null;
  const radius = size >= 64 ? 12 : 14;
  return (
    <div data-testid={stripTestid} style="display:flex;gap:8px;flex-wrap:wrap">
      {urls.map((url, i) => (
        <img
          key={url}
          data-testid={`${itemTestid}-${i}`}
          src={url}
          alt="Captured photo"
          style={`width:${size}px;height:${size}px;object-fit:cover;border-radius:${radius}px;border:1px solid var(--mark-border-default);flex:none`}
        />
      ))}
    </div>
  );
}
